/**
 * DeleteSedSuggestionAlert — NS-3 SDL-FOLLOWUP-AUTO-SUGGEST-EMPTY-SED.
 *
 * When a site has:
 *   - a SED on file (site.sedId set), AND
 *   - zero user-uploaded docs, AND
 *   - zero reports
 *
 * …its SED is most likely a stale artefact from a half-aborted setup
 * (the bootstrap fired, but the user never followed up with docs or
 * reports). NS-3 surfaces a small dismissable Alert on the Site
 * Evidence tab suggesting the user consider deleting the SED to start
 * fresh. The Alert routes the user to the Actions menu on the Site
 * Evidence header (scroll + focus); it does NOT auto-delete — the
 * auto-delete — the destructive action stays one explicit confirm-gate
 * away (SDL-3 dialog).
 *
 * Admin opt-in:
 *   The suggestion is only meaningful to operators who own the
 *   site-lifecycle decision. Non-admin members see nothing — they
 *   can't delete the SED anyway (the SDL-1 button is admin-gated at
 *   the host) and the Alert would just add chrome.
 *
 * Dismissal:
 *   Tracked per-site in localStorage under
 *   `delete-sed-suggestion-dismissed-<siteId>`. Dismissal is sticky —
 *   if the user later uploads a doc or generates a report the
 *   condition stops holding and the Alert disappears organically;
 *   if they then delete everything again it WILL re-appear unless
 *   they re-dismiss.
 *
 *   localStorage is best-effort: a write failure (quota, private
 *   mode) is silently caught — the Alert just won't survive a refresh.
 *
 * Composition with SDL-1:
 *   The Alert's CTA looks up the Actions menu trigger by its stable
 *   `data-testid="site-evidence-actions-menu-trigger"` testid; clicking
 *   the CTA scrolls the header into view + focuses the trigger +
 *   flashes a brief ring for visual continuity. This keeps the
 *   non-destructive contract intact (Hard Rule 8) — the user still
 *   has to click the Delete SED button to open the SDL-3 dialog.
 */
import React from 'react';
import { Lightbulb, X } from 'lucide-react';

interface Props {
  /** Site id — used to namespace the localStorage dismissal key. */
  siteId: string;
  /** Whether the current user is an admin. Non-admins see nothing. */
  isAdmin: boolean;
  /** Does the site have a SED on file? When false there's nothing to
   *  suggest deleting. */
  hasSed: boolean;
  /** Count of user-uploaded documents on this site. */
  docCount: number;
  /** Count of reports for this site. */
  reportCount: number;
  /** Optional override scroll-target testid (defaults to the SDL-1
   *  Delete SED button's testid). Kept overrideable so the same
   *  Alert can be re-used in a future surface without a code edit. */
  scrollTargetTestId?: string;
}

const DISMISS_KEY_PREFIX = 'delete-sed-suggestion-dismissed-';

/** Read the dismissed-flag from localStorage. Best-effort — returns
 *  `false` when storage is unavailable (private mode, quota). */
function readDismissed(siteId: string): boolean {
  try {
    return window.localStorage.getItem(`${DISMISS_KEY_PREFIX}${siteId}`) === '1';
  } catch {
    return false;
  }
}

/** Write the dismissed-flag to localStorage. Best-effort. */
function writeDismissed(siteId: string): void {
  try {
    window.localStorage.setItem(`${DISMISS_KEY_PREFIX}${siteId}`, '1');
  } catch {
    /* quota / private mode — Alert just won't survive a refresh. */
  }
}

/**
 * Scrolls the Delete SED button into view + flashes a focus ring.
 *
 * Intentionally non-destructive: we DO NOT click the button — the
 * SDL-3 dialog opens via an explicit user click. The scroll + focus
 * pattern mirrors SET-B1's doc-row receiver (see SiteLandingPage).
 */
function routeToDeleteButton(testId: string): void {
  if (typeof document === 'undefined') return;
  // Use querySelector with escaped attribute value to support the
  // stable testid contract.
  let target: HTMLElement | null = null;
  try {
    target = document.querySelector<HTMLElement>(`[data-testid="${testId}"]`);
  } catch {
    target = null;
  }
  if (!target) return;
  try {
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } catch {
    /* old browsers — fall through to focus. */
  }
  try {
    target.focus({ preventScroll: true });
  } catch {
    /* no-op. */
  }
  // Visual continuity ring — matches SET-B1's highlight pattern.
  try {
    target.classList.add('ring-2', 'ring-warning', 'ring-offset-2');
    window.setTimeout(() => {
      try {
        target?.classList.remove('ring-2', 'ring-warning', 'ring-offset-2');
      } catch { /* DOM may have been recycled. */ }
    }, 2400);
  } catch {
    /* no-op. */
  }
}

export const DeleteSedSuggestionAlert: React.FC<Props> = ({
  siteId,
  isAdmin,
  hasSed,
  docCount,
  reportCount,
  scrollTargetTestId = 'site-evidence-actions-menu-trigger',
}) => {
  // Hydrate from localStorage on first mount so the Alert stays hidden
  // across refreshes once the user has dismissed it for this site.
  // Lazy initialiser — only runs once per mount.
  const [dismissed, setDismissed] = React.useState<boolean>(() => readDismissed(siteId));

  // Re-hydrate when the site flips so the same component instance
  // (rare — the host re-mounts the tab on site change) doesn't carry
  // a stale dismissed flag from a previous site.
  React.useEffect(() => {
    setDismissed(readDismissed(siteId));
  }, [siteId]);

  // Visibility gate. Each clause is a short-circuit so the cheapest
  // check runs first (admin role, then hasSed, then counts).
  if (!isAdmin) return null;
  if (!hasSed) return null;
  if (docCount > 0 || reportCount > 0) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    writeDismissed(siteId);
    setDismissed(true);
  };

  const handleRouteToDelete = () => {
    routeToDeleteButton(scrollTargetTestId);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-start gap-2 px-3 py-2 rounded-md border border-warning/40 bg-warning-subtle text-[10px]"
      data-testid="delete-sed-suggestion-alert"
    >
      <Lightbulb
        aria-hidden
        className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-px"
      />
      <div className="flex-1 min-w-0">
        <div className="font-semibold uppercase tracking-widest text-amber-700 type-body">
          Empty site
        </div>
        <p className="text-text-primary leading-snug mt-0.5">
          This site has no documents and no reports — its SED may be a
          leftover from an aborted setup. Consider deleting it to start
          fresh; existing reports (none here) would keep their snapshot,
          and Rebuild SED is one click away.
        </p>
        <div className="mt-1.5 flex items-center gap-2">
          <button
            type="button"
            onClick={handleRouteToDelete}
            className="type-body font-bold uppercase tracking-widest px-2 py-1 border border-amber-400 text-amber-700 hover:bg-amber-100"
            data-testid="delete-sed-suggestion-route"
          >
            Consider deleting SED
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="type-body uppercase tracking-widest text-text-muted hover:text-text-primary"
            data-testid="delete-sed-suggestion-dismiss-link"
          >
            Not now
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss suggestion"
        className="text-text-muted hover:text-text-primary shrink-0 -mr-1"
        data-testid="delete-sed-suggestion-dismiss"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};
